import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, AlertCircle } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/dnh/SiteChrome";
import { useAuth } from "../components/dnh/AuthContext";
import { safeReturnPath } from "../utils/safeReturnPath";

/* ============================================================
   DNH — Sign In  (/signin)
   Supports both email/password login AND Google OAuth.
   REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
   ============================================================ */

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const handleGoogleSignIn = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Backend initiates the OAuth flow — redirect_uri is configured server-side
  const from = safeReturnPath(new URLSearchParams(window.location.search).get("from"));
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  window.location.href = `${backendUrl}/api/auth/google?from_path=${encodeURIComponent(from)}`;
};

const BrandPanel = () => (
  <div data-testid="signin-brand-panel" className="relative h-full flex flex-col justify-between bg-[var(--ink)] px-10 py-12 xl:px-14 xl:py-14">
    {/* Subtle grid texture */}
    <div
      className="absolute inset-0 opacity-[0.035]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(250,248,244,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(250,248,244,0.5) 1px, transparent 1px)",
        backgroundSize: "40px 40px",
      }}
      aria-hidden
    />
    {/* Crimson left accent */}
    <div className="absolute inset-y-0 left-0 w-[3px]" style={{ background: "var(--crimson)" }} aria-hidden />

    {/* Logo */}
    <div className="relative">
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[rgba(250,248,244,0.38)] mb-1.5">
        Daily News Highlights
      </div>
      <div className="font-serif text-[32px] leading-none text-[rgba(250,248,244,0.95)] tracking-[-0.015em]">
        DNH
      </div>
    </div>

    {/* Statement */}
    <div className="relative">
      <p data-testid="signin-brand-quote" className="font-serif text-[26px] sm:text-[28px] xl:text-[32px] leading-[1.18] text-[rgba(250,248,244,0.95)] tracking-[-0.012em]">
        &ldquo;Intelligence isn&apos;t a feed.{" "}
        <span data-testid="signin-brand-accent" className="text-[var(--crimson)]">It&apos;s a frame.&rdquo;</span>
      </p>
      <p className="font-sans text-[14px] leading-[1.65] text-[rgba(250,248,244,0.48)] mt-5 max-w-[38ch]">
        Read reporting across countries, topics, and continuous
        time — so you never mistake noise for news again.
      </p>
    </div>

    {/* Features */}
    <ul className="relative space-y-2.5">
      {[
        "Source-linked reporting in English",
        "Clearly labeled AI-assisted analysis",
        "Scheduled news updates",
        "Dated briefs and topic timelines",
      ].map((f) => (
        <li key={f} className="flex items-center gap-2.5">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
            style={{ background: "var(--crimson)" }}
          />
          <span className="font-sans text-[13px] text-[rgba(250,248,244,0.48)]">{f}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function SignIn() {
  const [showPwd, setShowPwd] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const from = safeReturnPath(params.get("from"));
  useEffect(() => {
    if(user) navigate(from, { replace: true });
  }, [user?.user_id, from, navigate]);

  // If already signed in, redirect
  if (user) {
    return (
      <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]" data-testid="signin-page">
        <SiteHeader activeNav="" />
        <main className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-sm">
            <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--muted)] mb-3">
              Already signed in
            </div>
            <h1 className="font-serif text-[32px] leading-[1.1] text-[var(--ink)] mb-4">
              Welcome back, {user.name?.split(" ")[0] || "friend"}.
            </h1>
            <p className="font-mono text-[11px] text-[var(--muted)] mb-4">Redirecting you back…</p>
            <Link
              data-testid="signin-return-link"
              to={from}
              className="inline-flex items-center gap-2 font-sans text-[14px] text-[var(--crimson)] hover:underline"
            >
              Continue <ArrowRight size={14} />
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        setError(Array.isArray(detail) ? detail.map((d) => d.msg).join(" ") : (detail || "Sign in failed"));
      } else {
        const signedIn = await checkAuth({ force: true });
        if(!signedIn) setError("Your session could not be confirmed. Please try signing in again.");
      }
    } catch {
      setError("Network error — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]" data-testid="signin-page">
      <SiteHeader activeNav="" ticker />

      <main className="flex">
        {/* Left: Brand panel */}
        <div className="hidden lg:block lg:w-[42%] xl:w-[38%] flex-shrink-0">
          <BrandPanel />
        </div>

        {/* Right: Form */}
        <div className="flex-1 flex flex-col">
          {/* Form container */}
          <div className="flex-1 flex items-center justify-center px-5 py-12 sm:px-10">
          <div className="w-full max-w-[420px]">
            {/* Heading */}
            <div className="mb-8">
              <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--muted)] mb-3">
                Daily News Highlights
              </div>
              <h1
                className="font-serif text-[34px] sm:text-[40px] leading-[1.05] tracking-[-0.015em] text-[var(--ink)]"
                data-testid="signin-title"
              >
                Welcome back.
              </h1>
              <p className="font-sans text-[14.5px] leading-[1.5] text-[var(--ink-2)] mt-2">
                Sign in to your intelligence brief.
              </p>
            </div>

            {/* Google OAuth */}
            <button
              type="button"
              data-testid="signin-google-button"
              onClick={handleGoogleSignIn}
              className="w-full h-12 rounded-full border border-[var(--hairline)] bg-[var(--paper)] font-sans text-[14px] text-[var(--ink)] flex items-center justify-center gap-3 hover:border-[var(--hairline-strong)] hover:bg-[var(--surface-2)] transition-colors"
            >
              <GoogleIcon />
              Continue with Google
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-[var(--hairline)]" />
              <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-[var(--muted)]">or sign in with email</span>
              <div className="flex-1 h-px bg-[var(--hairline)]" />
            </div>

            {/* Error */}
            {error && (
              <div
                className="mb-4 flex items-start gap-2.5 rounded-[8px] border border-[var(--crimson)]/30 bg-[var(--crimson)]/5 px-4 py-3"
                data-testid="signin-error"
              >
                <AlertCircle size={15} strokeWidth={2} className="text-[var(--crimson)] shrink-0 mt-[1px]" />
                <p className="font-sans text-[13px] text-[var(--crimson)] leading-[1.5]">{error}</p>
              </div>
            )}

            {/* Email + password form */}
            <form className="space-y-4" data-testid="signin-form" onSubmit={handleEmailLogin}>
              {/* Email */}
              <div>
                <label
                  htmlFor="signin-email"
                  className="block font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--muted)] mb-1.5"
                >
                  Email
                </label>
                <input
                  id="signin-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="signin-email-input"
                  className="w-full h-12 px-4 border border-[var(--hairline)] rounded-[10px] bg-[var(--paper)] font-sans text-[15px] text-[var(--ink)] placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--ink)] transition-colors"
                />
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label
                    htmlFor="signin-password"
                    className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--muted)]"
                  >
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="font-mono text-[10px] tracking-[0.12em] uppercase text-[var(--muted)] hover:text-[var(--crimson)] transition-colors"
                    data-testid="signin-forgot-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="signin-password"
                    type={showPwd ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="signin-password-input"
                    className="w-full h-12 px-4 pr-11 border border-[var(--hairline)] rounded-[10px] bg-[var(--paper)] font-sans text-[15px] text-[var(--ink)] placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--ink)] transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    data-testid="signin-toggle-password"
                  >
                    {showPwd ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                data-testid="signin-submit-button"
                className="w-full h-12 rounded-full bg-[var(--ink)] text-[var(--paper)] font-sans text-[14px] font-medium flex items-center justify-center gap-2 hover:bg-[var(--crimson)] transition-colors mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Signing in…" : "Sign in"}
                {!loading && <ArrowRight size={15} strokeWidth={2} />}
              </button>
            </form>

            {/* Sign up link */}
            <p className="mt-8 text-center font-sans text-[13.5px] text-[var(--muted)]">
              New to DNH?{" "}
              <Link
                to={`/signup?from=${encodeURIComponent(from)}`}
                className="text-[var(--ink)] font-medium hover:text-[var(--crimson)] transition-colors"
                data-testid="signin-signup-link"
              >
                Start your free account &rarr;
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-6 text-center">
          <div className="flex items-center justify-center gap-5">
            {["Privacy", "Terms", "Support"].map((l) => (
              <a
                key={l}
                href="#" onClick={(e) => e.preventDefault()}
                className="font-mono text-[10px] tracking-[0.14em] uppercase text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
              >
                {l}
              </a>
            ))}
          </div>
          <p className="font-mono text-[10px] text-[var(--muted)] mt-2">
            &copy; 2026 Daily News Highlights
          </p>
        </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
